import {ApolloDataParams, ApolloDataSubscribeParams, getQueryKey} from './apollo.worker.shared';
import {ApolloClient, ApolloError, HttpLink, InMemoryCache, ObservableQuery} from '@apollo/client/core';
import type {environment} from '@env/environment';
import {Subscription} from 'rxjs';
import {NodejsWorker} from '../nodejs-worker';
import queries from './queries';
import {createPersistedQueryLink} from '@apollo/client/link/persisted-queries';


export class ApolloWorkerAdapter extends NodejsWorker {
  protected subscriptions: Record<string, Subscription> = {}

  protected clients: Record<string, ApolloClient<any>>;

  protected environment?: typeof environment;

  messageEventListener() {
    return async ({data}: { data: ApolloDataParams }) => {
      if (data.type === 'init') {
        this.initClients(data.environment, data.isPreview, data.hostname, data.isBrowser);
        return;
      }

      const key = getQueryKey(data.queryType, data.query);


      if (data.type === 'unsubscribe') {
        this.subscriptions[key]?.unsubscribe();
        delete this.subscriptions[key];
      }

      if (data.type === 'subscribe') {
        this.messageSubscribeHandler(key, data)
      }

    }
  }

  protected postError(key: string, error: any) {
    const errorData = {} as Record<any, any>;

    if (error instanceof Error) {
      errorData['isError'] = true;
      errorData['message'] = error.message;
      errorData['stack'] = error.stack;
      errorData['name'] = error.name;
    }

    if (error instanceof ApolloError) {
      errorData['isApolloError'] = true;
      errorData['graphQLErrors'] = error.graphQLErrors;
      errorData['networkError'] = error.networkError;
      errorData['extraInfo'] = error.extraInfo;
    }

    this.internalPostMessage({
      key,
      error: errorData
    })
  }

  protected messageSubscribeHandler(key: string, data: ApolloDataSubscribeParams) {

    // Prevent universal from ending the request until first response
    let interval = setInterval(() => {
    }, 1000);

    if (this.subscriptions[key]) {
      throw new Error('currently only support one query at a time');
    }

    const queryFn = queries[data.query.queryName];
    if (!queryFn) {
      throw new Error('Query not found: ' + data.query.queryName);
    }


    const query = queryFn()
    const client = this.clients[data.query.client || 'default'];
    const apolloFn = client[data.queryType] as any;

    if (!apolloFn) {
      throw new Error('Apollo function not found: ' + data.queryType);
    }

    let options = {
      [data.queryType === 'mutate' ? 'mutation' : 'query']: query,
      variables: data.query.variables ?? {},
      ...data.apolloParams
    } as Record<string, any>;

    const apolloFnResult = apolloFn(options);

    if (typeof apolloFnResult.then !== 'undefined') {
      apolloFnResult
        .then((res: any) => {
          this.internalPostMessage({
            key,
            data: res
          })
        })
        .finally(() => {
          clearInterval(interval);
        })
        .catch((err: any) => this.postError(key, err))

    } else if (typeof (apolloFnResult as ObservableQuery).subscribe !== 'undefined') {
      this.subscriptions[key] = (apolloFnResult as ObservableQuery).subscribe({
        next: (res) => {
          clearInterval(interval);
          if (res.error && res.data === undefined) {
            const apolloError = res.error
            this.postError(key, apolloError);
            return;
          }
          this.internalPostMessage({
            key,
            data: res
          })
        },
        error: (err) => {
          clearInterval(interval);
          this.postError(key, err);
        }
      }) as any
    } else {
      throw new Error('unsupported apollo function result: ' + data.queryType);
    }
  }

  protected initClients = (environmentData: typeof environment, isPreview = false, hostname?: string, isBrowser = true) => {
    if (this.clients) {
      return;
    }

    if (environmentData) {
      this.environment = environmentData;
    }

    let defaultClient = this.defaultClient(hostname, isBrowser);

    if (isPreview) {
      defaultClient = this.postClient();
    }

    this.clients = {
      default: defaultClient,
      'post-query': this.postClient(),
      superpay: this.superpayClient(),
    }
  }

  protected defaultClient(hostname?: string, isBrowser = true) {
    if (!this.environment) {
      throw new Error('environment not set');
    }
    const hashFunction = async (query: string) => {
      const buffer = new TextEncoder().encode(query);
      const hash = await crypto.subtle.digest('SHA-256', buffer);
      return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
    }
    const persistedQueriesLink = createPersistedQueryLink({sha256: hashFunction, useGETForHashedQueries: true});
    return this.client({
      link: persistedQueriesLink.concat(
        new HttpLink({
          uri: this.environment.supervin.api + this.environment.supervin.graphQLPath + '?hostname=' + hostname,
          credentials: 'include'
        })
      ),
    })
  }

  protected postClient() {
    return this.client()
  }

  protected superpayClient() {
    if (!this.environment) {
      throw new Error('environment not set');
    }
    return this.client({
      link: new HttpLink({
        uri: this.environment.superpay.graphql,
        useGETForQueries: false,
      }),
    })
  }

  private client(override: any = {}) {
    if (!this.environment) {
      throw new Error('environment not set');
    }
    const fetchPolicy = this.isBrowser ? 'cache-and-network' : 'network-only';
    return new ApolloClient({
      cache: new InMemoryCache(),
      ssrMode: true,
      link: new HttpLink({
        uri: this.environment.supervin.api + this.environment.supervin.graphQLPath,
        useGETForQueries: false,
        credentials: 'include'
      }),
      defaultOptions: {
        watchQuery: {
          context: {
            fetchOptions: {
              method: 'POST',
            },
          },
          errorPolicy: 'all',
          fetchPolicy: fetchPolicy,
        }
      },
      ...override
    });
  }

}
